import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Badge, Button, Card, Text, Title } from '@tremor/react';
import { RiArrowLeftLine, RiCalendarLine, RiFileDownloadLine } from '@remixicon/react';
import { Pill } from 'evergreen-ui';

import { generateCandidateCvSummary, getCandidateById } from '../../network/ats';
import { normalizeCandidateDetails } from './utils/candidate';
import { ScheduleInterviewDialog } from './components/Dialogs/ScheduleInterviewDialog';

export default function CandidateDetail() {
  const navigate = useNavigate();
  const params = useParams();
  const candidateId = params.id;
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [cvSummaryError, setCvSummaryError] = useState<string | null>(null);

  const {
    data: response,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => getCandidateById(String(candidateId)),
    enabled: !!candidateId,
  });

  const candidate = useMemo(() => {
    const raw = (response as any)?.data ?? response;
    if (!raw) return null;
    return normalizeCandidateDetails(raw);
  }, [response]);

  useEffect(() => {
    if (candidate?.cvSummary?.text) {
      setCvSummaryError(null);
    }
  }, [candidate?.cvSummary?.text]);

  const handleGenerateSummary = async (refresh?: boolean) => {
    if (!candidateId || isGeneratingSummary) return;
    setIsGeneratingSummary(true);
    setCvSummaryError(null);

    try {
      await generateCandidateCvSummary(String(candidateId), {
        refresh: Boolean(refresh),
      });
      await refetch();
    } catch (error: any) {
      setCvSummaryError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to generate CV summary.',
      );
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  if (!candidateId) {
    return (
      <div className="p-6">
        <Card>
          <Title>Candidate details</Title>
          <Text className="mt-2">Missing candidate id.</Text>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => navigate('/ats')}>
              Back to ATS
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <ScheduleInterviewDialog
        isOpen={isScheduleOpen}
        onClose={() => setIsScheduleOpen(false)}
        user={{ id: candidateId }}
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            icon={RiArrowLeftLine}
            iconPosition="left"
            onClick={() => navigate('/ats')}
          >
            Back
          </Button>
          <div>
            <Title>Candidate details</Title>
            <Text className="mt-1 text-sm text-gray-600">
              View candidate profile and resume.
            </Text>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            icon={RiCalendarLine}
            onClick={() => setIsScheduleOpen(true)}
          >
            Schedule interview
          </Button>
          <Button variant="secondary" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </div>

      {isLoading && (
        <Card>
          <Text>Loading candidate…</Text>
        </Card>
      )}

      {isError && (
        <Card className="border border-red-200 bg-red-50">
          <Title>Failed to load candidate</Title>
          <Text className="mt-2">
            Please try again. If the issue persists, verify the candidate exists
            and your permissions are correct.
          </Text>
          <div className="mt-4">
            <Button variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      )}

      {!isLoading && !isError && candidate && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <Title>{candidate.name || 'Unnamed candidate'}</Title>
                <Text className="mt-1 text-sm text-gray-600">
                  {candidate.position ? candidate.position : '—'}
                  {candidate.department ? ` • ${candidate.department}` : ''}
                  {candidate.location ? ` • ${candidate.location}` : ''}
                </Text>
              </div>
              {candidate.status && (
                <Badge color="orange" className="capitalize">
                  {candidate.status}
                </Badge>
              )}
            </div>

            <div className="mt-4">
              <Title>Summary</Title>
              <Text className="mt-2 whitespace-pre-wrap">
                {candidate.description || 'No description provided.'}
              </Text>
            </div>

            <div className="mt-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Title>CV summary</Title>
                <Button
                  variant="secondary"
                  onClick={() =>
                    handleGenerateSummary(Boolean(candidate.cvSummary?.text))
                  }
                  loading={isGeneratingSummary}
                  disabled={isGeneratingSummary}
                >
                  {candidate.cvSummary?.text ? 'Regenerate summary' : 'Generate summary'}
                </Button>
              </div>
              {candidate.cvSummary?.text ? (
                <>
                  <Text className="mt-2 whitespace-pre-wrap">
                    {candidate.cvSummary.text}
                  </Text>
                  {candidate.cvSummary.highlights?.length ? (
                    <ul className="mt-3 list-disc pl-5 text-sm text-gray-700">
                      {candidate.cvSummary.highlights.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : null}
                </>
              ) : (
                <Text className="mt-2 text-sm text-gray-600">
                  No CV summary yet. Generate one to quickly review highlights.
                </Text>
              )}
              {cvSummaryError && (
                <Text className="mt-2 text-sm text-red-600">
                  {cvSummaryError}
                </Text>
              )}
            </div>

            {candidate.coverLetter && (
              <div className="mt-6">
                <Title>Cover letter</Title>
                <Text className="mt-2 whitespace-pre-wrap">
                  {candidate.coverLetter}
                </Text>
              </div>
            )}
          </Card>

          <Card>
            <Title>Resume</Title>
            {candidate.resume?.url ? (
              <div className="mt-3 space-y-2">
                <Text className="text-sm text-gray-700">
                  {candidate.resume?.title || 'Resume'}
                </Text>
                <a
                  href={candidate.resume.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  <RiFileDownloadLine />
                  Open / download
                </a>
              </div>
            ) : (
              <Text className="mt-2 text-sm text-gray-600">
                No resume uploaded.
              </Text>
            )}

            <div className="mt-6">
              <Title>Skills</Title>
              <div className="mt-2 flex flex-wrap gap-2">
                {candidate.skills?.length ? (
                  candidate.skills.map((s) => (
                    <Pill key={s} color="green">
                      {s}
                    </Pill>
                  ))
                ) : (
                  <Text className="text-sm text-gray-600">No skills listed.</Text>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

